import { toast } from '@neo/primitives/use-toast'
import type { ApiResponse } from '@shared/types'
import type { MutationOptions } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { AlertTriangle, CircleCheck, CircleX, Info } from 'lucide-react'
import { TOAST_TIMEOUT } from 'v2/configs/constants'
import { formatToastDescription } from 'v2/shared/utils'
import { getErrorText } from 'v2/shared/utils/api-error-text'

// Create a toast for a mutation
interface ToastProps {
  title: string
  description?: string | JSX.Element | JSX.Element[]
  callback?: (error?: Error | unknown) => void
  onlyCallback?: boolean
}

export const successToast = ({
  title,
  description,
  callback,
  onlyCallback = false,
}: ToastProps) => {
  if (onlyCallback) {
    callback?.()
    return
  }
  const toastDetails = toast({
    title,
    description,
    icon: <CircleCheck size={16} className="text-status-success" />,
  })
  toastDetails.dismiss(TOAST_TIMEOUT.api)
  callback?.()
  return toastDetails
}

export const errorToast = ({ title, description, callback }: ToastProps) => {
  toast({
    title,
    description: formatToastDescription(description),
    variant: 'destructive',
    icon: <CircleX size={16} className="text-destructive" />,
  }).dismiss(TOAST_TIMEOUT.api)
  callback?.()
}

export const infoToast = ({ title, description, callback }: ToastProps) => {
  toast({
    title,
    description,
    icon: <Info size={16} className="text-brand-text" />,
    variant: 'info',
  }).dismiss(TOAST_TIMEOUT.api)
  callback?.()
}

export const warningToast = ({ title, description, callback }: ToastProps) => {
  toast({
    title,
    description,
    variant: 'warning',
    icon: <AlertTriangle size={16} className="text-status-warning" />,
  }).dismiss(TOAST_TIMEOUT.api)
  callback?.()
}

export const generateToastsForMutation = <TResponse, TPayload>(
  success: ToastProps,
  errorProps: ToastProps,
): MutationOptions<TResponse, Error, TPayload> => ({
  onSuccess: (data) => {
    if (success.callback) success.callback = success.callback.bind(null, data)
    successToast(success)
  },
  onError: (error) => {
    const errorText = `${getErrorText(error as AxiosError<ApiResponse<TResponse>>)}`.trim()
    errorToast({
      title: errorProps.title,
      description: errorText
        ? `${errorText.replace(/^\d+\.\s*/, '')}. ${errorProps.description}`
        : errorProps.description,
      callback: () => errorProps.callback?.(error),
    })
  },
})
